<template>
  <Dialog
    :open="modelValue"
    as="div"
    class="relative z-50 text-gray-700"
    @close="emit('update:modelValue', false)"
  >
    <div
      class="fixed inset-0 bg-black/30"
      aria-hidden="true"
    />
    <div class="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel class="flex h-auto w-full max-w-md flex-col rounded bg-gray-50 md:max-w-lg">
        <div class="flex-none rounded-t border-b border-gray-300 bg-gray-50 p-2 md:p-4">
          <button
            class="mb-2 ml-auto flex focus:outline-none"
            @click="emit('update:modelValue', false)"
          >
            <XMarkIcon class="size-6 fill-gray-700" />
          </button>
          <DialogTitle class="mb-3 text-center font-serif text-xl text-gray-700 md:text-3xl">
            Crear descuento - Paso 1 de 2
          </DialogTitle>
        </div>
        <div class="flex-1 overflow-y-auto">
          <DialogDescription class="px-2 py-4 text-sm font-light text-gray-700 md:px-4 md:text-base">
            <form
              class="flex h-full flex-col"
              @submit="onSubmit"
            >
              <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div class="flex w-full flex-col">
                  <label class="mb-2 text-sm md:text-base">Nombre del descuento</label>
                  <Field
                    v-model="name"
                    type="text"
                    label="Nombre del descuento"
                    class="w-full rounded-md border bg-white p-2 focus:outline-none"
                    name="name"
                    :rules="{ required: true }"
                    placeholder="Ej: Descuento de invierno"
                  />
                  <ErrorMessage
                    name="name"
                    class="mt-1 text-sm text-red-500"
                  />
                </div>
                <div class="flex w-full flex-col">
                  <label class="mb-2 text-sm md:text-base">Porcentaje de descuento</label>
                  <div class="flex w-full items-center rounded-md border text-sm md:text-base">
                    <Field
                      v-model="makerDiscount"
                      type="number"
                      label="Porcentaje de descuento"
                      class="w-full rounded-md rounded-r-none bg-white p-2 focus:outline-none"
                      name="makerDiscount"
                      :rules="{ required: true, min_value: 1, max_value: 100 }"
                      placeholder="15"
                    />
                    <span class="ml-auto rounded-md rounded-l-none bg-white p-2 text-gray-400">%</span>
                  </div>
                  <ErrorMessage
                    name="makerDiscount"
                    class="mt-1 text-sm text-red-500"
                  />
                </div>
              </div>
              <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div class="flex w-full flex-col">
                  <label class="mb-2 text-sm md:text-base">Fecha de inicio</label>
                  <lokal-datepicker
                    v-model="startDate"
                    :placeholder="'Seleccionar fecha'"
                    :auto-apply="true"
                    :range="false"
                    :min-date="new Date()"
                  />
                  <Field
                    v-model="startDate"
                    label="Fecha de inicio"
                    name="startDate"
                    :rules="{ required: true }"
                    hidden
                  />
                  <ErrorMessage
                    name="startDate"
                    class="mt-1 text-sm text-red-500"
                  />
                </div>
                <div class="flex w-full flex-col">
                  <label class="mb-2 text-sm md:text-base">Fecha de fin</label>
                  <lokal-datepicker
                    v-model="endDate"
                    :auto-apply="true"
                    :range="false"
                    :min-date="startDate"
                  />
                  <Field
                    v-model="endDate"
                    type="text"
                    label="Fecha de fin"
                    name="endDate"
                    :rules="{ required: true }"
                    hidden
                  />
                  <ErrorMessage
                    name="endDate"
                    class="mt-1 text-sm text-red-500"
                  />
                </div>
              </div>
              <div class="mb-4 rounded-md bg-blue-50 p-3">
                <p class="text-sm text-blue-800 md:text-base">
                  En el siguiente paso podrás seleccionar los productos que tendrán este descuento.
                </p>
              </div>
            </form>
          </DialogDescription>
        </div>
        <div class="flex-none rounded-b border-t border-gray-300 bg-gray-50 p-2 md:p-4">
          <div class="flex justify-between gap-2">
            <button
              type="button"
              class="w-full rounded-md border border-gray-300 bg-white p-2 text-sm font-normal text-gray-700 hover:bg-gray-50 md:text-base"
              @click="emit('update:modelValue', false)"
            >
              Cancelar
            </button>
            <lokal-loading
              v-if="isLoading"
              size="xs"
              :loading="isLoading"
              class="w-full justify-center"
            />
            <button
              v-else
              type="submit"
              class="w-full rounded-md border border-lk-green bg-white p-2 text-sm font-normal text-lk-green hover:bg-lk-green-deep hover:text-white md:text-base"
              :disabled="!name || !makerDiscount"
              :class="{ 'cursor-not-allowed opacity-50': !name || !makerDiscount }"
              @click="onSubmit"
            >
              Siguiente
            </button>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script lang="ts" setup>
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Field, useForm, ErrorMessage } from 'vee-validate';
import { ref, computed } from 'vue';
import { Dialog, DialogPanel, DialogTitle, DialogDescription } from '@headlessui/vue';
import { useMutation } from 'vue-query';
import discountsApi from 'api/discounts';
import lokalLoading from './shared/lokal-loading.vue';
import lokalDatepicker from './shared/lokal-datepicker.vue';

interface MakersDiscountNewDialogProps {
  modelValue: boolean;
  makerId?: number;
}

const props = withDefaults(defineProps<MakersDiscountNewDialogProps>(), {
  modelValue: false,
  makerId: undefined,
});

const emit = defineEmits<{
  'reload-discounts': [];
  'update:modelValue': [value: boolean];
  'discount-created': [discount: Discount];
}>();

const oneWeekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
const name = ref('');
const makerDiscount = ref<number | undefined>(undefined);
const startDate = ref(new Date());
const endDate = ref(oneWeekFromNow);

function resetForm() {
  name.value = '';
  makerDiscount.value = undefined;
  startDate.value = new Date();
  endDate.value = oneWeekFromNow;
}

const data = computed(() => ({
  makerId: props.makerId!,
  name: name.value,
  makerDiscount: makerDiscount.value!,
  startDate: startDate.value?.toISOString() || '',
  endDate: endDate.value?.toISOString() || '',
}));

const createMutation = useMutation(() => discountsApi.create(data.value), {
  onSuccess: (response) => {
    const createdDiscount = response.data.discount as Discount;
    emit('discount-created', createdDiscount);
    emit('update:modelValue', false);
    resetForm();
  },
});

const isLoading = computed(() => createMutation.isLoading.value);

const { handleSubmit } = useForm();

const onSubmit = handleSubmit(() => {
  createMutation.mutate();
});
</script>
